import { useEffect, useState, useCallback } from "react";
import MainLayout from "@/components/Layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Lightbulb, Refrigerator, ChefHat, Wind, Shirt, Monitor, HelpCircle, Settings, TrendingDown } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { HouseholdSurveyDialog } from "@/components/HouseholdSurveyDialog";
import { useToast } from "@/hooks/use-toast";

interface ConsumptionBreakdown {
  lighting_kwh: number;
  refrigeration_kwh: number;
  cooking_kwh: number;
  heating_cooling_kwh: number;
  washing_kwh: number;
  electronics_kwh: number;
  other_kwh: number;
  confidence_scores: Record<string, number>;
}

const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  lighting: Lightbulb,
  refrigeration: Refrigerator,
  cooking: ChefHat,
  heating_cooling: Wind,
  washing: Shirt,
  electronics: Monitor,
  other: HelpCircle
};

const categoryColors = {
  lighting: "hsl(var(--chart-1))",
  refrigeration: "hsl(var(--chart-2))",
  cooking: "hsl(var(--chart-3))",
  heating_cooling: "hsl(var(--chart-4))",
  washing: "hsl(var(--chart-5))",
  electronics: "hsl(var(--primary))",
  other: "hsl(var(--muted))"
};

export default function ApplianceInsights() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [breakdown, setBreakdown] = useState<ConsumptionBreakdown | null>(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Check if user has completed household profile
      const { data: profile } = await supabase
        .from('user_household_profiles')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      setHasProfile(!!profile);

      // Fetch latest consumption breakdown
      const { data: breakdownData } = await supabase
        .from('inferred_consumption_breakdown')
        .select('*')
        .eq('user_id', user?.id)
        .order('recorded_at', { ascending: false })
        .limit(1)
        .single();

      if (breakdownData) {
        setBreakdown(breakdownData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, fetchData]);

  const runAnalysis = async () => {
    if (!user) return;
    
    setIsAnalyzing(true);
    try {
      const { data: userData } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      await supabase.functions.invoke('analyze-consumption-patterns', {
        body: {
          userId: user.id,
          organizationId: userData?.organization_id,
          periodType: 'daily'
        }
      });

      toast({
        title: "Analysis Complete",
        description: "Your appliance consumption has been analyzed."
      });

      await fetchData();
    } catch (error) {
      console.error('Error running analysis:', error);
      toast({
        title: "Analysis Failed",
        description: "Unable to analyze consumption patterns.",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getConfidenceBadge = (score: number) => {
    if (score >= 80) return <Badge variant="default">High Confidence</Badge>;
    if (score >= 50) return <Badge variant="secondary">Medium Confidence</Badge>;
    return <Badge variant="outline">Low Confidence</Badge>;
  };

  const pieData = breakdown ? [
    { name: 'Lighting', value: Number(breakdown.lighting_kwh), category: 'lighting' },
    { name: 'Refrigeration', value: Number(breakdown.refrigeration_kwh), category: 'refrigeration' },
    { name: 'Cooking', value: Number(breakdown.cooking_kwh), category: 'cooking' },
    { name: 'Heating/Cooling', value: Number(breakdown.heating_cooling_kwh), category: 'heating_cooling' },
    { name: 'Washing', value: Number(breakdown.washing_kwh), category: 'washing' },
    { name: 'Electronics', value: Number(breakdown.electronics_kwh), category: 'electronics' },
    { name: 'Other', value: Number(breakdown.other_kwh), category: 'other' }
  ].filter(item => item.value > 0) : [];

  const totalConsumption = pieData.reduce((sum, item) => sum + item.value, 0);

  const recommendations = breakdown ? [
    {
      category: 'lighting',
      value: Number(breakdown.lighting_kwh),
      tip: 'Switch to LED bulbs to reduce lighting costs by up to 75%'
    },
    {
      category: 'heating_cooling',
      value: Number(breakdown.heating_cooling_kwh),
      tip: 'Set your AC to 24-25°C to optimize energy usage'
    },
    {
      category: 'refrigeration',
      value: Number(breakdown.refrigeration_kwh),
      tip: 'Keep refrigerator at 3-4°C and avoid overloading'
    }
  ].sort((a, b) => b.value - a.value).slice(0, 3) : [];

  return (
    <MainLayout title="Appliance Insights">
      <div className="space-y-6 p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Appliance Insights</h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Understand which appliances consume the most energy
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!hasProfile && (
              <Button onClick={() => setShowSurvey(true)} variant="outline" className="flex-1 md:flex-none">
                <Settings className="mr-2 h-4 w-4" />
                Complete Profile
              </Button>
            )}
            <Button onClick={runAnalysis} disabled={isAnalyzing} className="flex-1 md:flex-none">
              {isAnalyzing ? 'Analyzing...' : 'Run Analysis'}
            </Button>
          </div>
        </div>

        {!hasProfile && (
          <Card className="border-primary/50 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Complete Your Household Profile
              </CardTitle>
              <CardDescription>
                Help us provide more accurate insights by completing a quick survey about your household.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => setShowSurvey(true)}>
                Start Survey
              </Button>
            </CardContent>
          </Card>
        )}

        {breakdown && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Daily Consumption Breakdown</CardTitle>
                  <CardDescription>
                    Total: {totalConsumption.toFixed(2)} kWh per day
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name}: ${entry.value.toFixed(1)} kWh`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={categoryColors[entry.category as keyof typeof categoryColors]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Category Details</CardTitle>
                  <CardDescription>Breakdown by appliance category</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {pieData.map((item) => {
                      const Icon = categoryIcons[item.category];
                      const confidence = breakdown.confidence_scores?.[item.category] || 0;
                      const percentage = (item.value / totalConsumption * 100).toFixed(1);
                      
                      return (
                        <div key={item.category} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <Icon className="h-5 w-5 text-primary flex-shrink-0" />
                            <div>
                              <p className="font-medium">{item.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {item.value.toFixed(2)} kWh/day ({percentage}%)
                              </p>
                            </div>
                          </div>
                          {getConfidenceBadge(confidence)}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-primary" />
                  Energy Saving Recommendations
                </CardTitle>
                <CardDescription>
                  Personalized tips based on your consumption patterns
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recommendations.map((rec, idx) => {
                    const Icon = categoryIcons[rec.category];
                    return (
                      <div key={idx} className="flex items-start gap-3 p-4 border rounded-lg bg-muted/30">
                        <Icon className="h-5 w-5 text-primary mt-0.5" />
                        <div>
                          <p className="font-medium capitalize">{rec.category.replace('_', ' ')}</p>
                          <p className="text-sm text-muted-foreground">{rec.tip}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {!breakdown && !isLoading && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">
                No consumption data available yet. Run an analysis to get started.
              </p>
              <Button onClick={runAnalysis} disabled={isAnalyzing}>
                {isAnalyzing ? 'Analyzing...' : 'Run Initial Analysis'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {user && (
        <HouseholdSurveyDialog 
          open={showSurvey} 
          onOpenChange={setShowSurvey}
          userId={user.id}
        />
      )}
    </MainLayout>
  );
}
